import type { PrintConfiguration, PrintResponseDTO } from 'types/templates';

export type CreatePrintConfigurationReq = Omit<PrintConfiguration, 'id'> & { templateId: string };
export type PatchPrintConfigurationReq = { id: string } & Omit<PrintConfiguration, 'id' | 'templateId'>;
import { heliosApi } from '.';
import { TAG_TYPES } from 'types/services';
import { parseErrorResponseBody } from 'utils/helios/errorResponseBody';

const getFileNameFromContentDisposition = (contentDisposition: string | null): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (simpleMatch?.[1]) {
    return simpleMatch[1].trim();
  }

  return undefined;
};

export const heliosPrintConfigurationsService = heliosApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPrintConfigurationsByTemplateId: builder.query<PrintConfiguration[], { templateId: string }>({
      query: ({ templateId }) => ({
        url: `PrintConfigurations/${templateId}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATIONS' }],
    }),
    getPrintConfigurationById: builder.query<PrintConfiguration, { id: string }>({
      query: ({ id }) => ({
        url: `PrintConfigurations/Configuration/${id}`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATION_DETAIL' }],
    }),
    createPrintConfiguration: builder.mutation<PrintConfiguration, CreatePrintConfigurationReq>({
      query: ({ templateId, ...body }) => ({
        url: 'PrintConfigurations',
        method: 'POST',
        body: { templateId, ...body },
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATIONS' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    patchPrintConfiguration: builder.mutation<PrintConfiguration, PatchPrintConfigurationReq>({
      query: ({ id, ...body }) => ({
        url: 'PrintConfigurations',
        method: 'PATCH',
        body: { id, ...body },
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATIONS' },
        { type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATION_DETAIL' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    deletePrintConfigurations: builder.mutation<void, { ids: string[] }>({
      query: ({ ids }) => ({
        url: 'PrintConfigurations',
        method: 'DELETE',
        params: { ids },
      }),
      invalidatesTags: [
        { type: TAG_TYPES.TEMPLATES, id: 'PRINT_CONFIGURATIONS' },
        { type: TAG_TYPES.TEMPLATES, id: 'DETAIL' },
      ],
    }),
    print: builder.mutation<PrintResponseDTO, { configurationId: string; records: number[] }>({
      query: ({ configurationId, ...body }) => ({
        url: 'PrintConfigurations/Print',
        method: 'POST',
        body: { configurationId, ...body },
        responseHandler: async (response) => {
          if (!response.ok) {
            return await parseErrorResponseBody(response);
          }

          const blob = await response.blob();
          const contentType = response.headers.get('Content-Type') ?? 'application/octet-stream';
          const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition')) ?? 'print';

          return await new Promise<PrintResponseDTO>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = typeof reader.result === 'string' ? reader.result : '';
              const base64 = result.split(',')[1] ?? '';
              if (!base64) {
                reject(new Error('Empty file payload'));
                return;
              }
              resolve({
                fileContent: base64,
                fileName,
                contentType,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        },
      }),
    }),
  }),
});

export const {
  useGetPrintConfigurationsByTemplateIdQuery,
  useGetPrintConfigurationByIdQuery,
  useCreatePrintConfigurationMutation,
  usePatchPrintConfigurationMutation,
  useDeletePrintConfigurationsMutation,
  usePrintMutation,
} = heliosPrintConfigurationsService;
